/**
 * Step · Verify Email — mandatory OTP gate for the Feb 2026 onboarding v2.
 * If the user's email is already verified (e.g. Google OAuth, Apple with
 * verified email), we show a green "verified" pill and enable Continue
 * immediately. Otherwise we send a 6-digit code via Resend and require it.
 *
 * Feb 2026 · Resend-sandbox detection. If the backend returns a Resend
 * "testing emails only" error, we surface a persistent red banner with the
 * exact remediation path (admin verifies domain on resend.com) instead of
 * a vague "couldn't send" toast. Mirrors the mobile copy.
 */
import { useEffect, useState } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { Mail, Loader2, CheckCircle, ArrowRight, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Match backend Resend sandbox error verbatim. Resend's message starts with
// "You can only send testing emails to your own email address" — that
// substring is the reliable signal since exact wording sometimes varies.
const SANDBOX_MATCH = /only send testing emails/i;

export default function StepVerifyEmail({ onNext }) {
  const { user, checkAuth } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [hasSent, setHasSent] = useState(false);

  const [sendError, setSendError] = useState(false);
  const [sandboxError, setSandboxError] = useState(false);

  const verified = !!user?.email_verified;

  const classifyError = (e) => {
    const detail = e?.response?.data?.detail || '';
    if (typeof detail === 'string' && SANDBOX_MATCH.test(detail)) {
      setSandboxError(true);
      setSendError(true);
      return null;  // don't toast — banner covers it
    }
    setSandboxError(false);
    setSendError(true);
    return detail || "Couldn't send code — tap to try again";
  };

  // Auto-send the first code on mount when email is unverified.
  useEffect(() => {
    if (verified || hasSent) return;
    let alive = true;
    (async () => {
      setSending(true);
      try {
        await api.post('/auth/resend-verification');
        if (alive) {
          setHasSent(true);
          setSendError(false);
          setSandboxError(false);
          setResendCountdown(60);
        }
      } catch (e) {
        if (alive) {
          const msg = classifyError(e);
          if (msg) toast.error(msg);
        }
      } finally {
        if (alive) setSending(false);
      }
    })();
    return () => { alive = false; };
  }, [verified, hasSent]);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const id = setInterval(() => setResendCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCountdown]);

  const verify = async () => {
    if (code.trim().length !== 6) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/verify-email', { code: code.trim() });
      toast.success('Email verified ✓');
      await checkAuth?.();
      onNext?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (resendCountdown > 0) return;
    setBusy(true);
    try {
      await api.post('/auth/resend-verification');
      toast.success('New code sent — check your inbox');
      setHasSent(true);
      setSendError(false);
      setSandboxError(false);
      setResendCountdown(60);
    } catch (e) {
      const msg = classifyError(e);
      if (msg) toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-6" data-testid="step-verify-email">
      <div>
        <h2 className="text-2xl font-bold mb-1">Verify your email</h2>
        <p className="text-zinc-500 text-sm">
          Keeps your account safe — required to start matching.
        </p>
      </div>

      {verified ? (
        <div className="bg-gradient-to-br from-[#F0FDF4] to-white border border-[#16A34A]/30 rounded-2xl p-5 flex items-center gap-4" data-testid="email-already-verified">
          <span className="w-12 h-12 rounded-full bg-[#16A34A]/10 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={22} className="text-[#16A34A]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-[#0F0F10]">Already verified</p>
            <p className="text-[12px] text-[#4B4B52] truncate">{user?.email}</p>
          </div>
        </div>
      ) : (
        <>
          {sandboxError && (
            <div
              className="bg-gradient-to-br from-[#FEF2F2] to-white border-2 border-[#B91C1C]/40 rounded-2xl p-4 flex items-start gap-3"
              data-testid="email-sandbox-banner"
              role="alert"
            >
              <span className="w-9 h-9 rounded-full bg-[#B91C1C]/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={16} className="text-[#B91C1C]" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-[#B91C1C] mb-1">Email delivery is in sandbox mode</p>
                <p className="text-[12px] text-[#4B4B52] leading-relaxed">
                  This account can&apos;t receive an OTP right now — ask an admin to verify a sender domain on <span className="font-mono text-[#B91C1C]">resend.com/domains</span>.
                </p>
              </div>
            </div>
          )}

          <div className="bg-gradient-to-br from-[#FEF2F2] to-white border border-[#B91C1C]/20 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-full bg-[#B91C1C]/10 flex items-center justify-center">
                {sending ? <Loader2 size={16} className="text-[#B91C1C] animate-spin" /> : <Mail size={16} className="text-[#B91C1C]" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-[#0F0F10]">Enter the 6-digit code</p>
                <p className="text-[12px] text-[#4B4B52] truncate">
                  Sent to <span className="font-semibold">{user?.email}</span>
                </p>
              </div>
            </div>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*"
              maxLength={6} placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full text-center font-mono text-2xl tracking-[0.5em] font-bold py-3 rounded-xl bg-white border border-[var(--hairline-strong)] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30"
              data-testid="verify-email-code-input"
              autoFocus
            />
            <button
              onClick={resend}
              disabled={busy || resendCountdown > 0}
              data-testid="verify-email-resend"
              className={`mt-3 text-[12px] font-semibold hover:underline disabled:opacity-50 disabled:no-underline ${
                sendError ? 'text-[#B45309]' : 'text-[#B91C1C]'
              }`}
            >
              {sandboxError
                ? 'Try sending again'
                : sendError
                  ? "Couldn't send — tap to try again"
                  : resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Didn't get it? Resend code"}
            </button>
          </div>
        </>
      )}

      <button
        onClick={verified ? onNext : verify}
        disabled={!verified && (busy || code.length !== 6)}
        data-testid="verify-email-next"
        className="w-full brand-gradient text-white py-3.5 rounded-xl font-semibold transition-all hover:shadow-[0_0_30px_rgba(255,51,120,0.2)] disabled:opacity-30 active:scale-[0.98] inline-flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
        {verified ? 'Continue' : 'Verify & Continue'}
        {!busy && <ArrowRight size={16} />}
      </button>
    </div>
  );
}
